import React, { useMemo, useState } from "react";
import { AlertTriangle, Check } from "lucide-react";
import { PageSeo } from "@/components/seo/PageSeo";
import { Input } from "@/components/ui/Input";
import { Textarea } from "@/components/ui/Textarea";
import { Select } from "@/components/ui/Select";
import { Button } from "@/components/ui/Button";
import { useToast } from "@/components/ui/Toast";
import {
  readPrefillArea,
  clearPrefillArea,
} from "@/hooks/use-contact-prefill";
import { offices, contactEmails } from "@/lib/nav-data";
import { useT } from "@/hooks/useT";
import { useT as useTFooter } from "@/hooks/useT";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AREA_KEYS = [
  "marketing_technology",
  "ai_infrastructure",
  "data_tokenization",
  "energy_green_compute",
  "partnership",
  "media",
];

const PERSONAL_DOMAINS = new Set([
  "gmail.com",
  "yahoo.com",
  "hotmail.com",
  "outlook.com",
  "icloud.com",
  "protonmail.com",
]);

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const INITIAL = {
  full_name: "",
  work_email: "",
  company: "",
  role: "",
  area_of_interest: "",
  message: "",
  honeypot: "",
};

function validate(values, tErr) {
  const errs = {};
  if (values.full_name.trim().length < 2) errs.full_name = tErr("fullNameShort");
  if (values.full_name.length > 120) errs.full_name = tErr("fullNameLong");
  if (!EMAIL_RE.test(values.work_email)) errs.work_email = tErr("emailInvalid");
  else {
    const domain = values.work_email.split("@")[1]?.toLowerCase();
    if (domain && PERSONAL_DOMAINS.has(domain)) errs.work_email = tErr("emailPersonal");
  }
  if (values.company.trim().length < 1) errs.company = tErr("companyRequired");
  if (values.company.length > 200) errs.company = tErr("companyLong");
  if (values.role.trim().length < 1) errs.role = tErr("roleRequired");
  if (values.role.length > 120) errs.role = tErr("roleLong");
  if (!values.area_of_interest) errs.area_of_interest = tErr("areaRequired");
  if (values.message.trim().length < 10) errs.message = tErr("messageShort");
  if (values.message.length > 5000) errs.message = tErr("messageLong");
  return errs;
}

export default function Contact() {
  const { t } = useT("contact");
  const { t: tf } = useTFooter("footer");
  const cityLabels = tf("cityLabels", { returnObjects: true }) || {};
  const emailLabels = tf("emailLabels", { returnObjects: true }) || {};

  const [values, setValues] = useState(() => ({
    ...INITIAL,
    area_of_interest: readPrefillArea() || "",
  }));
  const [state, setState] = useState({ status: "idle", banner: null });
  const [showErrors, setShowErrors] = useState(false);
  const toast = useToast();

  const tErr = (key) => t(`form.errors.${key}`);
  const errors = useMemo(() => validate(values, tErr), [values, t]); // eslint-disable-line react-hooks/exhaustive-deps
  const clientValid = Object.keys(errors).length === 0;

  const areaOptions = AREA_KEYS.map((k) => ({
    value: k,
    label: t(`form.areaOptions.${k}`),
  }));

  const setField = (name) => (e) => {
    const value = e.target.value;
    setValues((v) => ({ ...v, [name]: value }));
    if (state.status === "error") setState({ status: "idle", banner: null });
  };

  const submit = async (e) => {
    e.preventDefault();
    setShowErrors(true);
    if (!clientValid || state.status === "loading") return;

    setState({ status: "loading", banner: null });
    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(values),
      });
      const data = await res.json().catch(() => ({}));

      if (res.status === 200) {
        setState({ status: "success", banner: null });
        toast.success(t("form.toast.success"));
        clearPrefillArea();
        return;
      }

      if (res.status === 400 && data?.detail?.error === "work_email_required") {
        setState({ status: "error", banner: null });
        return;
      }
      if (res.status === 400 || res.status === 422) {
        setState({ status: "error", banner: t("form.banner.fields") });
        return;
      }
      if (res.status === 429) {
        setState({ status: "error", banner: t("form.banner.rate") });
        return;
      }
      setState({ status: "error", banner: t("form.banner.generic") });
    } catch (err) {
      setState({ status: "error", banner: t("form.banner.generic") });
    }
  };

  return (
    <>
      <PageSeo
        title="Contact — Let's Talk"
        description="Book a demo, ask about partnership, or reach the team. Offices in Singapore and Kuala Lumpur. General, partnerships and media enquiries welcome."
        path="/contact"
        image="/og/contact.png"
      />

      <section
        className="container-frac pt-24 pb-24 md:pt-32"
        data-testid="page-contact"
      >
        <span className="eyebrow" data-testid="page-eyebrow">
          {t("hero.eyebrow")}
        </span>
        <h1
          className="mt-6 max-w-4xl text-balance text-4xl font-bold leading-tight tracking-tight text-ink-primary sm:text-5xl lg:text-6xl"
          data-testid="page-h1"
        >
          {t("hero.h1")}
        </h1>
        <p className="mt-6 max-w-2xl text-lg leading-normal text-ink-secondary">
          {t("hero.body")}
        </p>
      </section>

      <section className="container-frac pb-32">
        <div className="grid gap-14 lg:grid-cols-[1.6fr_1fr]">
          <div>
            {state.status === "success" ? (
              <SuccessCard t={t} />
            ) : (
              <form
                className="grid gap-6"
                onSubmit={submit}
                noValidate
                aria-label={t("form.ariaLabel")}
                data-testid="contact-form"
              >
                {state.banner && (
                  <div
                    role="alert"
                    className="flex items-start gap-3 rounded-lg border border-red-500/40 bg-red-500/5 p-4"
                    data-testid="contact-form-banner"
                  >
                    <AlertTriangle
                      className="mt-0.5 h-4 w-4 shrink-0 text-red-400"
                      strokeWidth={2}
                    />
                    <p className="text-sm text-red-300">{state.banner}</p>
                  </div>
                )}

                <Input
                  id="contact-full-name"
                  label={t("form.fields.fullName")}
                  onDark
                  value={values.full_name}
                  onChange={setField("full_name")}
                  error={showErrors ? errors.full_name : null}
                  autoComplete="name"
                  disabled={state.status === "loading"}
                  data-testid="contact-full-name"
                />

                <Input
                  id="contact-work-email"
                  label={t("form.fields.workEmail")}
                  type="email"
                  placeholder={t("form.fields.workEmailPlaceholder")}
                  onDark
                  value={values.work_email}
                  onChange={setField("work_email")}
                  error={showErrors ? errors.work_email : null}
                  autoComplete="email"
                  disabled={state.status === "loading"}
                  data-testid="contact-work-email"
                />

                <div className="grid gap-6 sm:grid-cols-2">
                  <Input
                    id="contact-company"
                    label={t("form.fields.company")}
                    onDark
                    value={values.company}
                    onChange={setField("company")}
                    error={showErrors ? errors.company : null}
                    autoComplete="organization"
                    disabled={state.status === "loading"}
                    data-testid="contact-company"
                  />
                  <Input
                    id="contact-role"
                    label={t("form.fields.role")}
                    onDark
                    value={values.role}
                    onChange={setField("role")}
                    error={showErrors ? errors.role : null}
                    autoComplete="organization-title"
                    disabled={state.status === "loading"}
                    data-testid="contact-role"
                  />
                </div>

                <Select
                  id="contact-area"
                  label={t("form.fields.area")}
                  onDark
                  value={values.area_of_interest}
                  onChange={setField("area_of_interest")}
                  error={showErrors ? errors.area_of_interest : null}
                  disabled={state.status === "loading"}
                  placeholder={t("form.fields.areaPlaceholder")}
                  options={areaOptions}
                  data-testid="contact-area-of-interest"
                />

                <Textarea
                  id="contact-message"
                  label={t("form.fields.message")}
                  onDark
                  value={values.message}
                  onChange={setField("message")}
                  error={showErrors ? errors.message : null}
                  disabled={state.status === "loading"}
                  placeholder={t("form.fields.messagePlaceholder")}
                  data-testid="contact-message"
                />

                <div className="honeypot" aria-hidden="true">
                  <label htmlFor="contact-honeypot">{t("form.honeypotLabel")}</label>
                  <input
                    id="contact-honeypot"
                    type="text"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={values.honeypot}
                    onChange={setField("honeypot")}
                  />
                </div>

                <div>
                  <Button
                    type="submit"
                    variant="primary-on-dark"
                    size="lg"
                    loading={state.status === "loading"}
                    className="w-full sm:w-auto"
                    data-testid="contact-submit"
                  >
                    {state.status === "loading" ? t("form.submitting") : t("form.submit")}
                  </Button>
                </div>
              </form>
            )}
          </div>

          <aside
            className="grid gap-10 self-start rounded-lg border border-hairline bg-surface-raised p-8"
            aria-label={t("aside.ariaLabel")}
          >
            <div data-testid="contact-offices">
              <h2 className="text-xs font-semibold uppercase tracking-caps text-eyebrow">
                {t("aside.offices")}
              </h2>
              <div className="mt-4 space-y-6">
                {offices.map((o) => (
                  <div
                    key={o.city}
                    data-testid={`contact-office-${o.city.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <div className="text-sm font-semibold text-ink-primary">
                      {cityLabels[o.city] || o.city}
                    </div>
                    <div className="mt-1 text-sm leading-normal text-ink-secondary">
                      {o.address}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div data-testid="contact-direct">
              <h2 className="text-xs font-semibold uppercase tracking-caps text-eyebrow">
                {t("aside.direct")}
              </h2>
              <ul className="mt-4 space-y-4">
                {contactEmails.map((c) => (
                  <li key={c.email}>
                    <a
                      href={`mailto:${c.email}`}
                      className="group block"
                      data-testid={`contact-direct-${c.label.toLowerCase()}`}
                    >
                      <div className="text-xs font-semibold uppercase tracking-caps text-ink-tertiary">
                        {emailLabels[c.label] || c.label}
                      </div>
                      <div className="mt-1 text-sm text-ink-primary transition-colors duration-fast ease-out group-hover:text-frac-violet">
                        {c.email}
                      </div>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}

function SuccessCard({ t }) {
  return (
    <div
      className="rounded-lg border border-active-border/60 bg-frac-violet/10 p-8"
      role="status"
      data-testid="contact-success"
    >
      <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-frac-violet text-frac-white">
        <Check className="h-5 w-5" strokeWidth={2} />
      </span>
      <h2 className="mt-4 text-2xl font-bold tracking-tight text-ink-primary">
        {t("success.h2")}
      </h2>
      <p className="mt-3 max-w-lg text-base leading-normal text-ink-secondary">
        {t("success.body")}{" "}
        <a
          href="mailto:hello@frac.io"
          className="text-frac-violet underline underline-offset-4 hover:text-[#8A1AE8]"
        >
          hello@frac.io
        </a>
        .
      </p>
    </div>
  );
}
